/**
 * Расшифровка общей суммы строки сравнения — МСК Газобетон.
 *
 * Своих расчётов не делает. Все числа уже посчитаны и лежат в строке сравнения:
 *   • состав материалов — materialsToShow, как его читает MaterialsTooltip;
 *   • доставка — deliveryForTooltip, как его читает DeliveryTooltip;
 *   • разгрузка — deliveryBreakdown, куда её положил серверный расчёт;
 *   • итог — та же сумма к оплате, что показана в колонке и по которой
 *     определяется метка минимальной цены.
 *
 * Умножение «количество × цена» в строках материалов — то самое, которое старая
 * подсказка уже выполняет при отрисовке: это формат строки, а не новый расчёт
 * цены. Итоги разделов берутся готовыми и заново не суммируются.
 */

const GAZOBETON_MARKER = 'газобетон';

const toNumber = (value) => {
  const text = String(value ?? '').trim().replace(',', '.');
  // Пустое значение — это «числа нет», а не ноль: Number('') молча даёт 0.
  if (text === '') {
    return null;
  }
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

const isGazobetonMaterial = (material) => (
  String(material?.category ?? '').trim().toLowerCase().includes(GAZOBETON_MARKER)
);

/**
 * Количество позиции так, как его показывает старая подсказка: у газобетона —
 * фактический объём (он уже округлён до полного поддона), у остальных — количество.
 */
export const getGazBreakdownQuantity = (material) => {
  if (isGazobetonMaterial(material)) {
    const fact = toNumber(material?.factVolume);
    if (fact !== null && fact > 0) {
      return fact;
    }
  }
  const quantity = toNumber(material?.quantity);
  return quantity !== null ? quantity : null;
};

/** Единственная категория доставки строки: у газобетона это всегда «gaz». */
const deliveryEntriesOf = (entry) => {
  const delivery = entry?.deliveryForTooltip;
  if (!delivery || typeof delivery !== 'object') {
    return [];
  }

  const categories = Array.isArray(entry?.categoriesToInclude) && entry.categoriesToInclude.length > 0
    ? entry.categoriesToInclude
    : Object.keys(delivery);

  return categories
    .map((category) => {
      const details = delivery[category]?.details;
      // У ЖБИ details приходит массивом групп — в области такого не бывает, но
      // читать данные нужно так же осторожно, как это делает DeliveryTooltip.
      const data = Array.isArray(details) ? details[0]?.deliveryData?.data : (details?.data || details);
      return data && typeof data === 'object' ? { category, data } : null;
    })
    .filter(Boolean);
};

/**
 * Километраж плеча доставки одной строки сравнения.
 *
 * Своего расчёта нет: расстояния приходят с сервера в том же объекте, откуда
 * подсказка берёт рейсы и цену за рейс. `distanceProdToClient` — плечо от завода
 * до адреса, по нему и считаются деньги; `distanceKadToClient` — расстояние от
 * ближайшей точки КАД, в цене МСК-газобетона не участвует и показывается
 * справочно. Самовывоз расстояния не имеет — там плеча нет.
 *
 * @param {object} entry — строка сравнения как есть
 * @returns {{prodToClient:number|null, kadToClient:number|null}|null}
 */
export const getGazDeliveryDistances = (entry) => {
  const data = deliveryEntriesOf(entry).map(({ data: item }) => item).find(Boolean);
  if (!data) {
    return null;
  }

  const prodToClient = toNumber(data.distanceProdToClient ?? data.distanceKm ?? data.distance);
  const kadToClient = toNumber(data.distanceKadToClient);
  if (prodToClient === null && kadToClient === null) {
    return null;
  }

  return { prodToClient, kadToClient };
};

/**
 * Разгрузка, если она посчитана.
 *
 * Берётся из deliveryBreakdown готовой суммой. Она уже входит в итог доставки
 * (totalCost = deliveryCost + unloadingCost), поэтому отдельной строкой она только
 * показывается — прибавлять её к итогу второй раз нельзя.
 */
export const getGazUnloadingLine = (data) => {
  const breakdown = data?.deliveryBreakdown;
  if (!breakdown) {
    return null;
  }

  const unloading = toNumber(breakdown.unloadingCost) || 0;
  const crane = toNumber(breakdown.craneUnloadingCost) || 0;
  const sum = unloading + crane;
  if (!(sum > 0)) {
    return null;
  }

  const method = String(breakdown.unloadMethod ?? '').trim();
  return {
    method: method || 'Разгрузка',
    sum,
    // Разгрузка уже сидит внутри суммы доставки — подпись обязана это сказать.
    includedInDelivery: true,
  };
};

/**
 * Разгрузка строки сравнения одной суммой, 0 — её нет.
 *
 * Та же строка разгрузки, что показывает расшифровка, только числом: она нужна и
 * цене за куб, которая разгрузку не включает. Своего расчёта нет — сумма пришла с
 * сервера и уже сидит внутри доставки.
 */
export const getGazUnloadingSum = (entry) => {
  const line = deliveryEntriesOf(entry)
    .map(({ data }) => getGazUnloadingLine(data))
    .find(Boolean);
  return line ? Number(line.sum) || 0 : 0;
};

/**
 * Расшифровка общей суммы одной строки сравнения.
 *
 * @param {object} entry — строка sortedGroupedData как есть
 * @param {number|null} payableTotal — итог строки, уже посчитанный представлением
 * @returns {object|null}
 */
export const buildGazTotalBreakdown = ({ entry, payableTotal } = {}) => {
  if (!entry) {
    return null;
  }

  const materials = (Array.isArray(entry?.materialsToShow) ? entry.materialsToShow : [])
    .map((material) => {
      const quantity = getGazBreakdownQuantity(material);
      const price = toNumber(material?.pricePerOne);
      return {
        name: String(material?.name ?? '').trim() || 'Без названия',
        quantity,
        unit: String(material?.unit ?? '').trim(),
        price,
        // Та же арифметика строки, что и в старой подсказке материалов.
        sum: quantity !== null && price !== null ? Math.ceil(quantity * price) : null,
      };
    });

  const delivery = deliveryEntriesOf(entry).map(({ category, data }) => ({
    category,
    transport: String(data?.bestMethod ?? '').trim() || '—',
    trips: toNumber(data?.numberOfTrips),
    costPerTrip: toNumber(data?.costPerTrip),
    sum: toNumber(data?.totalCost),
  }));

  const unloading = deliveryEntriesOf(entry)
    .map(({ data }) => getGazUnloadingLine(data))
    .find(Boolean) || null;

  return {
    materials,
    // Итоги разделов — готовые суммы строки, а не пересчёт по позициям.
    materialsTotal: toNumber(entry?.totalMaterialCostForDisplay),
    delivery,
    deliveryTotal: toNumber(entry?.totalDeliveryCost),
    unloading,
    total: toNumber(payableTotal),
    hasDeliveryError: Boolean(entry?.hasDeliveryError),
  };
};

export default buildGazTotalBreakdown;
